import cn from 'classnames';
import React, { memo, useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Spinner } from 'components';
import { useNotification } from 'hooks/useNotification';
import { useCompanyCampaigns } from 'hooks/UseCompanyCampaigns';
import { useEvent } from 'hooks/useEvent';
import { API_ROUTES } from 'lib/api-routes';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useCommonList } from 'lib/contexts/common-list-context';
import {
  JobAttributes,
  JobRequestStep1,
  JobRequestStep2,
  JobRequestStep3,
} from 'lib/models/job';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { TouchpointState } from 'lib/models/opportunity';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';

import styles from './Job.module.scss';

export interface FieldStateOption {
  id: string;
  value: string;
  isHovered: boolean;
  toBeFocussed?: boolean;
}

export interface JobProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const Job: React.FC<JobProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: JobProps) => {
    const contentClassNames = cn(
      {
        [styles.Job]: true,
      },
      className
    );
    const router = useRouter();
    const notificationInstance = useNotification();
    const { step: postingStep, id: touchpointId, type } = router.query;
    const step = postingStep ? Number(postingStep) : undefined;

    const [isLoading, setLoading] = useState(false);
    const [isDraftLoading, setDraftLoading] = useState(false);
    const [touchpoint, setTouchpoint] = useState<JobAttributes | null>(null);

    const {
      areaOfResponsibilities,
      internshipKindOptions,
      educationRequirementsOptions,
      currencyOptions,
      cityCountryList,
    } = useCommonList();
    const {
      getCompanyJobState,
      createJob,
      updateJobStep1,
      updateJobStep2,
      updateJobStep3,
    } = useCompanyCampaigns();
    const { mutateCompanyCampaigns } = useCampaigns();
    const { questionTypes } = useEvent();

    useEffect(() => {
      if (!step || ((step === 2 || step === 3) && !touchpointId)) {
        router.replace(`${router.asPath}?step=1`);
      }
    }, [step, touchpointId]);

    useEffect(() => {
      const handlePopstate = () => {
        if (
          window.history.state?.edit &&
          window.location.pathname.includes(
            PAGE_ROUTES.HIRING_EDIT.replace('[id]', touchpointId as string)
          )
        ) {
          router.replace(
            `${PAGE_ROUTES.HIRING_EDIT.replace(
              '[id]',
              touchpointId as string
            )}?step=1&type=${type}`
          );
        }
      };
      window.addEventListener('popstate', handlePopstate);
      return () => {
        window.removeEventListener('popstate', handlePopstate);
      };
    }, []);

    useEffect(() => {
      (async () => {
        if (router.pathname === PAGE_ROUTES.HIRING_EDIT) {
          const { id } = router.query;
          if (id) {
            const pathname = `${API_ROUTES.COMPANY_TOUCHPOINTS}/${id}`;
            const state = await getCompanyJobState(pathname);
            setTouchpoint(state as JobAttributes);
          }
        }
      })();
    }, [router]);

    const { id, title } = touchpoint || {};

    const pathname = useMemo(() => {
      if (router.pathname === PAGE_ROUTES.HIRING_EDIT) {
        const { id } = router.query;
        return id ? PAGE_ROUTES.HIRING_EDIT.replace('[id]', id as string) : '';
      } else {
        return router.pathname;
      }
    }, [router, id]);

    const onStep1Completion = async (
      data: JobRequestStep1,
      isValueEdited: boolean,
      goToStep: number
    ) => {
      setLoading(true);
      const type = data?.touchpointable_attributes.type;
      try {
        const continueStep = () => {
          if (data?.submitType === TouchpointState.SAVE) {
            notificationInstance.success({
              title: 'Changes saved!',
              message: `${title} has been saved successfully.`,
            });
            mutateCompanyCampaigns();
            router.push(PAGE_ROUTES.HIRING);
          } else {
            router.push(`${pathname}?step=${goToStep}&type=${type}`);
          }
        };

        if (touchpoint && id) {
          if (!isValueEdited) {
            setLoading(false);
            continueStep();
          } else {
            const response = await updateJobStep1({ touchpoint: data, id });
            if (response) {
              setTouchpoint(response);
              setLoading(false);
              continueStep();
            } else {
              setLoading(false);
            }
          }
        } else {
          const response = await createJob({ touchpoint: data });
          if (response) {
            const id = response.id;
            setTouchpoint(response);
            setLoading(false);
            if (data?.submitType === TouchpointState.SAVE) {
              notificationInstance.success({
                title: 'Changes saved!',
                message: `${title} has been saved successfully.`,
              });
              mutateCompanyCampaigns();
              router.push(PAGE_ROUTES.HIRING);
            } else {
              const path = `${PAGE_ROUTES.HIRING_EDIT.replace('[id]', id)}`;
              window.history.replaceState(
                { edit: true },
                '',
                `${path}?step=1&type=${type}`
              );
              router.push(`${path}?step=${goToStep}&type=${type}`);
            }
          } else {
            setLoading(false);
          }
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };

    const onStep2Completion = async (
      data: JobRequestStep2,
      isValueEdited: boolean,
      goToStep: number
    ) => {
      setLoading(true);
      try {
        const continueStep = () => {
          if (data?.submitType === TouchpointState.SAVE) {
            notificationInstance.success({
              title: 'Changes Saved!',
              message: `${title} has been saved as draft successfully.`,
            });
            mutateCompanyCampaigns();
            router.push(PAGE_ROUTES.HIRING);
          } else {
            router.push(`${pathname}?step=${goToStep}&type=${type}`);
          }
        };

        if (touchpoint && id) {
          if (!isValueEdited) {
            setLoading(false);
            continueStep();
          } else {
            const response = await updateJobStep2({ touchpoint: data, id });
            if (response) {
              setTouchpoint(response);
              setLoading(false);
              continueStep();
            } else {
              setLoading(false);
            }
          }
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };

    const onStep3Completion = async ({
      data,
      isValueEdited,
      goToStep,
      isPreviewClicked,
    }: {
      data: JobRequestStep3;
      isValueEdited: boolean;
      goToStep?: number;
      isPreviewClicked?: boolean;
    }) => {
      if (data?.submitType === TouchpointState.DRAFT) {
        setDraftLoading(true);
      } else {
        setLoading(true);
      }
      try {
        const continueStep = () => {
          if (isPreviewClicked) return;
          if (
            data?.submitType === TouchpointState.SAVE ||
            data?.submitType === TouchpointState.DRAFT
          ) {
            if (data?.submitType === TouchpointState.SAVE) {
              notificationInstance.success({
                title: 'Changes saved!',
                message: `${title} has been saved successfully.`,
              });
            } else if (data?.submitType === TouchpointState.DRAFT) {
              notificationInstance.success({
                title: 'Job saved!',
                message: `${title} has been saved to drafts successfully.`,
              });
            }
            mutateCompanyCampaigns();
            router.push(`${PAGE_ROUTES.HIRING}/${id}`);
          } else if (goToStep) {
            router.push(`${pathname}?step=${goToStep}&type=${type}`);
          } else {
            notificationInstance.success({
              title: 'Job submitted!',
              message: `${title} has been submitted successfully.`,
            });
            mutateCompanyCampaigns();
            router.push(`${PAGE_ROUTES.HIRING}/${id}`);
          }
        };

        if (touchpoint && id) {
          if (!isValueEdited) {
            setDraftLoading(false);
            setLoading(false);
            continueStep();
          } else {
            const response = await updateJobStep3({
              touchpoint: data,
              id,
            });
            if (response) {
              setTouchpoint(response);
              setDraftLoading(false);
              setLoading(false);
              continueStep();
            } else {
              setDraftLoading(false);
              setLoading(false);
            }
          }
        }
      } catch (error) {
        setDraftLoading(false);
        setLoading(false);
        console.error(error);
      }
    };

    if (
      !areaOfResponsibilities ||
      !internshipKindOptions ||
      (router.pathname === PAGE_ROUTES.HIRING_EDIT && !touchpoint) ||
      !cityCountryList ||
      !educationRequirementsOptions ||
      !currencyOptions
    ) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    return (
      <div className={contentClassNames} style={style} data-testid="Job">
        {Number(step) === 1 ? (
          <Step1
            step={1}
            touchpoint={touchpoint}
            areaOfResponsibilities={areaOfResponsibilities}
            internshipKindOptions={internshipKindOptions}
            cityCountryList={cityCountryList}
            onSave={onStep1Completion}
            isLoading={isLoading}
          />
        ) : null}
        {Number(step) === 2 ? (
          <Step2
            step={2}
            touchpoint={touchpoint}
            educationRequirementsOptions={educationRequirementsOptions}
            currencyOptions={currencyOptions}
            onSave={onStep2Completion}
            isLoading={isLoading}
          />
        ) : null}
        {Number(step) === 3 ? (
          <Step3
            step={3}
            touchpoint={touchpoint}
            onSave={onStep3Completion}
            isDraftLoading={isDraftLoading}
            isLoading={isLoading}
            questionTypes={questionTypes}
          />
        ) : null}
      </div>
    );
  }
);

Job.displayName = 'Job';
